/**
 * Contextual actions menu for question instances.
 *
 * Defines the action descriptors (edit, copy link, attach document,
 * lock/unlock) and assembles them into `<QuestionActionsMenu>` using the
 * shared `ActionsMenu` UI component.
 */

import {
    Copy as CopyIcon,
    DocumentAttachment,
    DocumentExport,
    DocumentImport,
    Edit,
    Locked as LockedIcon,
    Unlocked as UnlockIcon,
    View as ViewIcon,
} from '@carbon/icons-react';
import type { SpaceStatus } from '@repo/shared/orpc/procedures/spaces';
import { EDITORIALDASH_MANAGE_CAPABILITY } from '@repo/shared/roles-capabilities';
import { CloseSpaceModal } from 'component/modal/question/close-space';
import { LinkedPartnerStoriesModal } from 'component/modal/question/linked-partner-stories-modal';
import { PublishSpaceModal } from 'component/modal/question/publish-space';
import { ReopenSpaceModal } from 'component/modal/question/reopen-space';
import { UnpublishSpaceModal } from 'component/modal/question/unpublish-space';
import { addNotificationToast } from 'component/notification-toast';
import type { ActionsMenuAction } from 'component/ui/actions-menu';
import { ActionsMenu } from 'component/ui/actions-menu';
import { updateSpaceAsync, useLinkedStories, useSpace } from 'data/spaces';
import { showApiErrorToast } from 'system/orpc-errors';
import { useHasCapability } from 'util/admin';
import type { Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';
import { makeUrlForInstance } from 'util/navigate';
import { type TLabel, t } from 'util/stdtypes';

type QuestionActionsMenuAction = ActionsMenuAction<{ instanceKey: string }>;

const ActionEdit: QuestionActionsMenuAction = {
    label: t('question.actions.edit_button', 'Edit'),
    icon: Edit,
    action: async ({ datastore, instanceKey }) => {
        datastore.gotoInstanceScreen({
            structureKey: 'question',
            instanceKey,
            screenKey: 'edit',
        });
    },
};

const ActionPreview: QuestionActionsMenuAction = {
    label: t('question.actions.preview_button', 'Preview'),
    icon: ViewIcon,
    action: async ({ datastore, instanceKey }) => {
        return datastore.gotoInstance({
            structureKey: 'question',
            instanceKey,
        });
    },
};

const ActionCopy: QuestionActionsMenuAction = {
    label: t('question.actions.copy_button', 'Copy'),
    icon: CopyIcon,
    action: async ({ datastore, instanceKey }) => {
        const url = makeUrlForInstance({ structureKey: 'question', instanceKey });
        fireAndForget(navigator.clipboard.writeText(url));
        if (datastore.props.appData) {
            addNotificationToast({
                appData: datastore.props.appData,
                type: 'success',
                label: t('question.actions.link_copied_toast', 'Link copied to clipboard!'),
            });
        }
    },
};

function LinkedStoriesModal({ onClose, instanceKey }: { onClose: () => void; instanceKey: string }) {
    const { linkedStories } = useLinkedStories(instanceKey);
    return <LinkedPartnerStoriesModal articles={linkedStories} onClose={onClose} />;
}

const ActionLinkedStories: QuestionActionsMenuAction = {
    label: t('question.actions.linked_stories_button', 'Linked stories'),
    icon: DocumentAttachment,
    action: () => {},
    modal: LinkedStoriesModal,
};

async function setSpaceStatusAsync(
    datastore: Datastore,
    instanceKey: string,
    status: SpaceStatus,
    successToast?: { label: TLabel; id: string },
) {
    const siloKey = datastore.getSiloKey();
    try {
        await updateSpaceAsync(datastore, { siloKey, spaceKey: instanceKey, status });
        if (successToast && datastore.props.appData) {
            addNotificationToast({
                appData: datastore.props.appData,
                type: 'success',
                label: successToast.label,
                id: successToast.id,
            });
        }
    } catch (error) {
        showApiErrorToast({ datastore, error });
    }
}

const ActionClose: QuestionActionsMenuAction = {
    label: t('question.actions.close_button', 'Close'),
    icon: LockedIcon,
    destructive: true,
    action: async ({ datastore, instanceKey }) => setSpaceStatusAsync(datastore, instanceKey, 'closed'),
    modal: CloseSpaceModal,
};

const ActionOpen: QuestionActionsMenuAction = {
    label: t('question.actions.reopen_button', 'Re-open'),
    icon: UnlockIcon,
    destructive: true,
    action: async ({ datastore, instanceKey }) => setSpaceStatusAsync(datastore, instanceKey, 'active'),
    modal: ReopenSpaceModal,
};

const ActionPublish: QuestionActionsMenuAction = {
    label: t('question.actions.publish_button', 'Publish'),
    icon: DocumentExport,
    action: async ({ datastore, instanceKey }) =>
        setSpaceStatusAsync(datastore, instanceKey, 'active', {
            label: t('question.actions.publish_success_toast', 'Space published'),
            id: 'space_published',
        }),
    modal: PublishSpaceModal,
};

const ActionUnpublish: QuestionActionsMenuAction = {
    label: t('question.actions.unpublish_button', 'Unpublish'),
    icon: DocumentImport,
    destructive: true,
    action: async ({ datastore, instanceKey }) =>
        setSpaceStatusAsync(datastore, instanceKey, 'draft', {
            label: t('question.actions.unpublish_success_toast', 'Space unpublished'),
            id: 'space_unpublished',
        }),
    modal: UnpublishSpaceModal,
};

type QuestionActionsMenuProps = {
    context: 'spaces' | 'question';
    instanceKey: string;
};

type ChooseQuestionMenuActionsParams = {
    context: 'spaces' | 'question';
    status: SpaceStatus | undefined;
    canManage: boolean;
};

/**
 * The actions a space's menu offers. Without `canManage` the status changes
 * (publish, unpublish, close, re-open) are listed but disabled.
 */
export function chooseQuestionMenuActions({ context, status, canManage }: ChooseQuestionMenuActionsParams): QuestionActionsMenuAction[] {
    const baseActions = context === 'spaces' ? [ActionEdit, ActionLinkedStories, ActionPreview, ActionCopy] : [ActionPreview, ActionCopy];
    const statusActions = chooseStatusActions({ context, status }).map((action) => (canManage ? action : { ...action, disabled: true }));
    return [...baseActions, ...statusActions];
}

function chooseStatusActions({ context, status }: Omit<ChooseQuestionMenuActionsParams, 'canManage'>): QuestionActionsMenuAction[] {
    switch (status) {
        case 'draft':
            return context === 'question' ? [ActionPublish] : [];
        case 'closed':
            return [ActionOpen];
        case 'active':
        default:
            return context === 'question' ? [ActionUnpublish, ActionClose] : [ActionClose];
    }
}

export function QuestionActionsMenu({ context, instanceKey }: QuestionActionsMenuProps) {
    const space = useSpace(instanceKey);
    const canManage = useHasCapability(EDITORIALDASH_MANAGE_CAPABILITY);
    const actions = chooseQuestionMenuActions({ context, status: space?.status, canManage });
    return <ActionsMenu actionArgs={{ instanceKey }} actions={actions} />;
}
